import { useEffect, useRef, type ReactNode } from 'react';
import { DiscardAsk, useDismissGuard } from '../dismissGuard.tsx';

/**
 * A contacts sheet: a native `<dialog>`, as the property modal and the facility board's, so Escape closes
 * it and focus stays inside. Wide enough for a form at desktop width, the full width less a gutter on a
 * tablet.
 */
export function Sheet({ title, onClose, children, labelId }: {
  title: ReactNode;
  onClose: () => void;
  children: ReactNode;
  labelId: string;
}) {
  const ref = useRef<HTMLDialogElement>(null);
  const closed = useRef(onClose);
  closed.current = onClose;
  useEffect(() => {
    const dialog = ref.current;
    if (!dialog) return;
    const handle = () => closed.current();
    dialog.addEventListener('close', handle);
    if (!dialog.open) dialog.showModal();
    return () => dialog.removeEventListener('close', handle);
  }, []);
  const dismiss = () => {
    if (ref.current?.open) ref.current.close();
    closed.current();
  };
  // A-#195: the backdrop only when pressed and released on it; changes are never discarded silently
  const guard = useDismissGuard(dismiss);
  return (
    <dialog
      ref={ref}
      aria-labelledby={labelId}
      {...guard.dialogProps}
      className="m-auto w-[min(44rem,calc(100vw-2rem))] rounded-xl bg-panel p-0 text-neutral-900 shadow-xl backdrop:bg-black/40"
    >
      <div className="max-h-[90vh] overflow-y-auto">
        <DiscardAsk guard={guard} />
        <div className="flex items-start gap-3 border-b border-neutral-200 px-4 py-3">
          <h2 id={labelId} className="min-w-0 flex-1 text-lg font-semibold text-neutral-900">{title}</h2>
          <button type="button" onClick={guard.request} className={secondary}>Close</button>
        </div>
        <div className="px-4 py-3">{children}</div>
      </div>
    </dialog>
  );
}

export const field =
  'mt-1 w-full min-h-[48px] rounded-lg border border-neutral-300 bg-white px-3 text-base text-neutral-900';
export const label = 'block text-sm font-semibold text-neutral-700';
export const hint = 'mt-1 text-sm text-neutral-600';
export const secondary =
  'min-h-[44px] rounded-lg border border-neutral-300 px-3 text-sm font-semibold ' +
  'text-neutral-800 hover:bg-neutral-50 active:bg-neutral-100';
export const primary =
  'min-h-[44px] rounded-lg bg-neutral-900 px-4 text-sm font-semibold text-white ' +
  'disabled:bg-neutral-200 disabled:text-neutral-600 disabled:cursor-not-allowed';

/** Blank is sent as null, never as ''. */
export function blank(s: string): string | null {
  const t = s.trim();
  return t ? t : null;
}
